import { BalanceData, EliteRule, LevelData } from "../data/balance";
import { Fix64 } from "../maths/fix64";
import { Fnv1a64 } from "../maths/fnv1a64";
import { EventRunJson, EventRunRule, eventRuleFromJson, eventRuleToJson, hashEventRule, isEventRunJson } from "./eventRun";
import { PetRule, PetRuleJson, WildPetRule, WildPetRuleJson, hashPetRule, hashWildPetRule, petRuleFromJson, petRuleToJson, wildPetRuleFromJson, wildPetRuleToJson } from "./petRule";

export type { BarkRule, BiteRule, HowlRule, PetRule, WildPetRule } from "./petRule";

/**
 * Run modifiers (DESIGN §5.1): what a run changes about a level without changing the level's data — the Weekly Trial's
 * featured affix, Heroic pressure on a cleared level, a test forcing an affix. They are inputs of the run like the
 * seed: the replay record carries them, and a run with none is bit-for-bit the run it always was (no hash change).
 */
export interface LevelModifiers {
  /** Replaces the level's own elite rules when present (an empty list: no elites at all); absent keeps them. */
  readonly elites?: readonly EliteRule[];
  /** Every spawn's HP (bosses and elites included) × this; One = unchanged. Heroic pressure. */
  readonly enemyHpMult: Fix64;
  /** An Iron challenge (docs/MODES.md): one long wave of every stage, only these towers in the draft. */
  readonly iron?: IronRule;
  /** An Endless run (docs/MODES.md): waves without end, scaled from the player's progress. */
  readonly endless?: EndlessRule;
  /** A Daily Road run (MP-20, docs/MODES.md §4): the day's road, its first `stages` stages, no rares, no chest. */
  readonly daily?: DailyRule;
  /** An event-mode run (MP-21, docs/MODES.md §5): a Boss Week tier, Race Week's road or an Odyssey map (sim/eventRun.ts). */
  readonly event?: EventRunRule;
  /** Pre-battle supplies taken into the run (MP-06): what each adds, copied from the data when the run starts. */
  readonly supplies?: SuppliesRule;
  /** A farm twist (MP-25): a replay of a cleared level with a Trial modifier (in `elites` / `enemyHpMult`) for more gold. */
  readonly farm?: FarmRule;
  /** A challenge-code run (MP-28, docs/MODES.md §7): the code it decodes from, an optional Fortress HP cap; no rares, no chest. */
  readonly challenge?: ChallengeRule;
  /** The pet taken into the run (docs/PETS.md): its id, level and abilities' numbers there, copied when the run starts (sim/petRule.ts). */
  readonly pet?: PetRule;
  /** Pets v2: the wild encounter's inputs (sim/wildPet.ts): which pet, the skin chance after a treat, its skin candidates. */
  readonly wildPet?: WildPetRule;
}

/**
 * Supplies (MP-06), each 0 when not taken: extra checkpoint retries (app suspend.ts), in-run hands shown ahead (the
 * HUD's sim/scout.ts), reroll charges at the start, and extra fortress troops for the first `hornWaves` waves.
 */
export interface SuppliesRule { readonly checkpoints: number; readonly scout: number; readonly rerolls: number; readonly hornTroops: number; readonly hornWaves: number }
/** A farm twist: the Trial modifier's id (for the record and the HUD) and the gold bonus on the battle chest of a win. */
export interface FarmRule { readonly modifier: string; readonly bonus: Fix64 }
export const NO_SUPPLIES_RULE: SuppliesRule = { checkpoints: 0, scout: 0, rerolls: 0, hornTroops: 0, hornWaves: 0 };

/** Iron: the only tower archetypes (indexes) the draft deals. */
export interface IronRule { readonly towers: readonly number[] }
/** Endless: the player's highest cleared level when the run started (its enemy pool and power anchor). */
export interface EndlessRule { readonly through: number }
/** Daily Road: the UTC day number it is the road of, and its stage count (the level's first `stages`, its boss last). */
export interface DailyRule { readonly day: number; readonly stages: number }
/** A challenge code's run: its canonical code (meta/challengeCode.ts) and the Fortress HP cap (0 = none). */
export interface ChallengeRule { readonly code: string; readonly livesCap: number }

export const NO_MODIFIERS: LevelModifiers = { enemyHpMult: Fix64.One };

/** What a run actually plays with: the level's rules merged with the modifiers, resolved once when the sim is built. */
export interface ResolvedModifiers {
  readonly elites: readonly EliteRule[];
  readonly enemyHpMult: Fix64;
  readonly iron: IronRule | null;
  readonly endless: EndlessRule | null;
  readonly daily: DailyRule | null;
  readonly event: EventRunRule | null;
  readonly supplies: SuppliesRule | null;
  readonly farm: FarmRule | null;
  readonly challenge: ChallengeRule | null;
  readonly pet: PetRule | null;
  readonly wildPet: WildPetRule | null;
  /**
   * Anything differs from a plain run of the level's data before affixes existed. Only then does the state hash carry
   * the modifier block and each enemy's affix, so every golden recorded without them keeps its hash.
   */
  readonly active: boolean;
}

export const PLAIN_RUN: ResolvedModifiers = { elites: [], enemyHpMult: Fix64.One, iron: null, endless: null, daily: null, challenge: null, event: null, supplies: null, farm: null, pet: null, wildPet: null, active: false };

/** Hash tags of the Iron and Endless blocks (written only when the run has them). */
const IRON_HASH_TAG = 0x1405, ENDLESS_HASH_TAG = 0xe1d5, DAILY_HASH_TAG = 0xda17, SUPPLIES_HASH_TAG = 0x5a99, FARM_HASH_TAG = 0xfa77, CHALLENGE_HASH_TAG = 0xc0de;

/** Whether a supplies block takes anything (an all-zero block is no supplies). */
const anySupply = (r: SuppliesRule | undefined): r is SuppliesRule => r !== undefined && (r.checkpoints > 0 || r.scout > 0 || r.rerolls > 0 || r.hornTroops > 0);

export function resolveModifiers(level: LevelData, m: LevelModifiers = NO_MODIFIERS): ResolvedModifiers {
  // An Endless run's elites come from the Endless rules (sim/runModes.ts), never the map level's own.
  const elites = m.endless ? [] : m.elites ?? level.elites;
  const iron = m.iron ?? null, endless = m.endless ?? null, daily = m.daily ?? null, challenge = m.challenge ?? null, event = m.event ?? null;
  const supplies = anySupply(m.supplies) ? m.supplies : null, farm = m.farm ?? null, pet = m.pet ?? null, wildPet = m.wildPet ?? null;
  const active = elites.length > 0 || !m.enemyHpMult.eq(Fix64.One) || iron !== null || endless !== null || daily !== null || challenge !== null || event !== null || supplies !== null || farm !== null || pet !== null || wildPet !== null;
  return active ? { elites, enemyHpMult: m.enemyHpMult, iron, endless, daily, challenge, event, supplies, farm, pet, wildPet, active } : PLAIN_RUN;
}

/** The modifier block of the state hash (written only for an active run; see ResolvedModifiers.active). */
export function hashModifiers(h: Fnv1a64, m: ResolvedModifiers): void {
  h.writeRaw(m.enemyHpMult.raw);
  h.writeInt(m.elites.length);
  for (const r of m.elites) { h.writeInt(r.affix); h.writeInt(r.every); h.writeInt(r.fromStage); }
  if (m.iron) { h.writeInt(IRON_HASH_TAG); h.writeInt(m.iron.towers.length); for (const t of m.iron.towers) h.writeInt(t); }
  if (m.endless) { h.writeInt(ENDLESS_HASH_TAG); h.writeInt(m.endless.through); }
  if (m.daily) { h.writeInt(DAILY_HASH_TAG); h.writeInt(m.daily.day); h.writeInt(m.daily.stages); }
  if (m.event) hashEventRule(h, m.event);
  if (m.supplies) { const r = m.supplies; h.writeInt(SUPPLIES_HASH_TAG); h.writeInt(r.checkpoints); h.writeInt(r.scout); h.writeInt(r.rerolls); h.writeInt(r.hornTroops); h.writeInt(r.hornWaves); }
  if (m.farm) { h.writeInt(FARM_HASH_TAG); h.writeString(m.farm.modifier); h.writeRaw(m.farm.bonus.raw); }
  if (m.challenge) { h.writeInt(CHALLENGE_HASH_TAG); h.writeString(m.challenge.code); h.writeInt(m.challenge.livesCap); }
  if (m.pet) hashPetRule(h, m.pet);
  if (m.wildPet) hashWildPetRule(h, m.wildPet);
}

/** Modifiers as stored in a replay record: affixes by id (an index would shift if `affixes` were reordered), HP raw. */
export interface LevelModifiersJson {
  elites?: { affix: string; every: number; fromStage: number }[];
  enemyHpMult: string;
  /** Iron: the towers by id. */
  iron?: { towers: string[] };
  endless?: { through: number };
  daily?: { day: number; stages: number };
  event?: EventRunJson;
  supplies?: SuppliesRule;
  /** The bonus as a raw Fix64 in decimal, like `enemyHpMult`. */
  farm?: { modifier: string; bonus: string };
  challenge?: { code: string; livesCap: number };
  pet?: PetRuleJson;
  wildPet?: WildPetRuleJson;
}

export function modifiersToJson(balance: BalanceData, m: LevelModifiers): LevelModifiersJson {
  const out: LevelModifiersJson = { enemyHpMult: m.enemyHpMult.raw.toString() };
  if (m.elites) out.elites = m.elites.map((r) => ({ affix: balance.affixes[r.affix].id, every: r.every, fromStage: r.fromStage }));
  if (m.iron) out.iron = { towers: m.iron.towers.map((t) => balance.towers[t].id) };
  if (m.endless) out.endless = { through: m.endless.through };
  if (m.daily) out.daily = { day: m.daily.day, stages: m.daily.stages };
  if (m.event) out.event = eventRuleToJson(balance, m.event);
  if (anySupply(m.supplies)) out.supplies = { ...m.supplies };
  if (m.farm) out.farm = { modifier: m.farm.modifier, bonus: m.farm.bonus.raw.toString() };
  if (m.challenge) out.challenge = { code: m.challenge.code, livesCap: m.challenge.livesCap };
  if (m.pet) out.pet = petRuleToJson(m.pet);
  if (m.wildPet) out.wildPet = wildPetRuleToJson(m.wildPet);
  return out;
}

/** Structural check of stored modifiers (no balance needed: affix ids are resolved on use by modifiersFromJson). */
export function isModifiersJson(v: unknown): v is LevelModifiersJson {
  if (typeof v !== "object" || v === null) return false;
  const j = v as Partial<LevelModifiersJson>;
  if (typeof j.enemyHpMult !== "string" || !/^\d+$/.test(j.enemyHpMult)) return false;
  if (j.iron !== undefined && (typeof j.iron !== "object" || j.iron === null || !Array.isArray(j.iron.towers) || !j.iron.towers.every((t) => typeof t === "string"))) return false;
  if (j.endless !== undefined && (typeof j.endless !== "object" || j.endless === null || !Number.isInteger(j.endless.through))) return false;
  if (j.daily !== undefined && !isDailyJson(j.daily)) return false;
  if (j.event !== undefined && !isEventRunJson(j.event)) return false;
  if (j.supplies !== undefined && !isSuppliesJson(j.supplies)) return false;
  if (j.farm !== undefined && !isFarmJson(j.farm)) return false;
  if (j.challenge !== undefined && !isChallengeJson(j.challenge)) return false;
  if (j.pet !== undefined && petRuleFromJson(j.pet) === null) return false;
  if (j.wildPet !== undefined && wildPetRuleFromJson(j.wildPet) === null) return false;
  if (j.elites === undefined) return true;
  return Array.isArray(j.elites) && j.elites.every((r) => typeof r === "object" && r !== null && typeof r.affix === "string" && Number.isInteger(r.every) && Number.isInteger(r.fromStage));
}

/** Parses stored modifiers; undefined for anything malformed or naming an affix this build does not have. */
export function modifiersFromJson(balance: BalanceData, v: unknown): LevelModifiers | undefined {
  if (typeof v !== "object" || v === null) return undefined;
  const j = v as Partial<LevelModifiersJson>;
  if (typeof j.enemyHpMult !== "string" || !/^\d+$/.test(j.enemyHpMult) || BigInt(j.enemyHpMult) <= 0n) return undefined;
  const enemyHpMult = Fix64.fromRaw(BigInt(j.enemyHpMult));
  let modes: Pick<LevelModifiers, "iron" | "endless" | "daily" | "challenge" | "event" | "supplies" | "farm" | "pet" | "wildPet"> = {};
  if (j.iron !== undefined) {
    if (typeof j.iron !== "object" || j.iron === null || !Array.isArray(j.iron.towers)) return undefined;
    const towers = j.iron.towers.map((id) => balance.towers.findIndex((t) => t.id === id && !t.isBase));
    if (towers.length === 0 || towers.some((t) => t < 0)) return undefined;
    modes = { iron: { towers } };
  }
  if (j.endless !== undefined) {
    const through = typeof j.endless === "object" && j.endless !== null ? j.endless.through : undefined;
    if (!Number.isInteger(through) || through! < 1 || through! > balance.levels.length) return undefined;
    modes = { ...modes, endless: { through: through! } };
  }
  if (j.daily !== undefined) {
    if (!isDailyJson(j.daily)) return undefined;
    modes = { ...modes, daily: { day: j.daily.day, stages: j.daily.stages } };
  }
  if (j.event !== undefined) {
    const event = eventRuleFromJson(balance, j.event);
    if (!event) return undefined;
    modes = { ...modes, event };
  }
  if (j.supplies !== undefined) {
    if (!isSuppliesJson(j.supplies)) return undefined;
    const r = j.supplies;
    modes = { ...modes, supplies: { checkpoints: r.checkpoints, scout: r.scout, rerolls: r.rerolls, hornTroops: r.hornTroops, hornWaves: r.hornWaves } };
  }
  if (j.farm !== undefined) {
    if (!isFarmJson(j.farm)) return undefined;
    modes = { ...modes, farm: { modifier: j.farm.modifier, bonus: Fix64.fromRaw(BigInt(j.farm.bonus)) } };
  }
  if (j.challenge !== undefined) {
    if (!isChallengeJson(j.challenge)) return undefined;
    modes = { ...modes, challenge: { code: j.challenge.code, livesCap: j.challenge.livesCap } };
  }
  if (j.pet !== undefined) {
    const pet = petRuleFromJson(j.pet);
    if (pet === null) return undefined;
    modes = { ...modes, pet };
  }
  if (j.wildPet !== undefined) {
    const wildPet = wildPetRuleFromJson(j.wildPet);
    if (wildPet === null) return undefined;
    modes = { ...modes, wildPet };
  }
  if (j.elites === undefined) return { enemyHpMult, ...modes };
  if (!Array.isArray(j.elites)) return undefined;
  const elites: EliteRule[] = [];
  for (const r of j.elites) {
    if (typeof r !== "object" || r === null) return undefined;
    const affix = balance.affixes.findIndex((a) => a.id === r.affix);
    if (affix < 0 || !Number.isInteger(r.every) || r.every < 1 || !Number.isInteger(r.fromStage) || r.fromStage < 1) return undefined;
    elites.push({ affix, every: r.every, fromStage: r.fromStage });
  }
  return { elites, enemyHpMult, ...modes };
}

/** True for the modifiers of a plain run (none set), which a record then leaves out. */
export function isPlainModifiers(m: LevelModifiers): boolean {
  return m.elites === undefined && m.enemyHpMult.eq(Fix64.One) && m.iron === undefined && m.endless === undefined && m.daily === undefined && m.challenge === undefined && m.event === undefined && !anySupply(m.supplies) && m.farm === undefined && m.pet === undefined && m.wildPet === undefined;
}

/** The run's modifiers without its supplies (a RETRY takes what is armed then, never the last run's for free). */
export function withoutSupplies(m: LevelModifiers): LevelModifiers {
  if (m.supplies === undefined) return m;
  const { supplies: _taken, ...rest } = m;
  return rest;
}

/** The run's modifiers without its pet and wild encounter (the pets feature puts today's into every new attempt). */
export function withoutPet(m: LevelModifiers): LevelModifiers {
  if (m.pet === undefined && m.wildPet === undefined) return m;
  const { pet: _left, wildPet: _wild, ...rest } = m;
  return rest;
}

const SUPPLY_KEYS = ["checkpoints", "scout", "rerolls", "hornTroops", "hornWaves"] as const;
/** A stored supplies block: five whole numbers, none negative. */
function isSuppliesJson(v: unknown): v is SuppliesRule {
  if (typeof v !== "object" || v === null) return false;
  const r = v as Record<string, unknown>;
  return SUPPLY_KEYS.every((k) => Number.isInteger(r[k]) && (r[k] as number) >= 0);
}
/** A stored farm twist: a modifier id and a raw bonus (decimal digits). */
function isFarmJson(v: unknown): v is { modifier: string; bonus: string } {
  if (typeof v !== "object" || v === null) return false;
  const f = v as { modifier?: unknown; bonus?: unknown };
  return typeof f.modifier === "string" && f.modifier.length > 0 && typeof f.bonus === "string" && /^\d+$/.test(f.bonus);
}

/** A stored Daily Road block: a day number (≥ 0) and a stage count (≥ 1). */
function isDailyJson(v: unknown): v is { day: number; stages: number } {
  if (typeof v !== "object" || v === null) return false;
  const d = v as { day?: unknown; stages?: unknown };
  return Number.isInteger(d.day) && (d.day as number) >= 0 && Number.isInteger(d.stages) && (d.stages as number) >= 1;
}

/** A stored challenge block: a code of code characters and a cap (0 = none). */
function isChallengeJson(v: unknown): v is { code: string; livesCap: number } {
  if (typeof v !== "object" || v === null) return false;
  const c = v as { code?: unknown; livesCap?: unknown };
  return typeof c.code === "string" && /^[0-9A-Z-]{1,40}$/.test(c.code) && Number.isInteger(c.livesCap) && (c.livesCap as number) >= 0;
}
